import React from "react";
import {connect} from "react-redux";
import {Alert, Button, Descriptions, Form, message, Modal, Select, Space, Tooltip} from "antd";
import {displayErrors} from "../../libs/utils";
import {loadSharedHostingPlans, modifyHostingAccount} from "../../actions/hosting";
import {MoneyField} from "../shared/MoneyField";
import moment from "moment/moment";

class ModifyHostingPlanModal extends React.Component {
    state = {
        loadingSharedHostingPlans: true,
        sharedHostingPlansLoaded: false,
        sharedHostingPlans: [],
        selectedHostingPlan: null,
        activePackage: null,
        step: 1,
        loading: false,
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (!prevProps.showModal && this.props.showModal && this.state.loadingSharedHostingPlans && !this.state.sharedHostingPlansLoaded) {
            this.loadSharedHostingPlans();
        }
    }

    loadSharedHostingPlans() {
        this.setState({ loadingSharedHostingPlans: true });

        this.props.loadSharedHostingPlans((res) => {
            this.setState({ loadingSharedHostingPlans: false, sharedHostingPlansLoaded: true, sharedHostingPlans: res.data.data }, () => {
                this.onPlanChange(this.props.activeHostingPlan.guid, () => {
                    if(this.props.activeHostingPlan !== null) {
                        this.setState({ selectedHostingPlan: this.props.activeHostingPlan.guid });
                    }
                });
            });
        }, (err) => {
            this.setState({ loadingSharedHostingPlans: false, sharedHostingPlansLoaded: true });

            if(typeof err.response !== 'undefined') {
                displayErrors(err.response.data);
            }
        });
    }

    closeModal(reload = false) {
        this.setState({
            loadingSharedHostingPlans: true,
            sharedHostingPlansLoaded: false,
            sharedHostingPlans: [],
            selectedHostingPlan: null,
            activePackage: null,
            step: 1,
            loading: false,
        });

        this.props.close();

        if(reload) {
            this.props.reloadHostingAccount();
        }
    }

    onPlanChange(value) {
        let activePackage = this.state.sharedHostingPlans.find(plan => plan.guid === value);

        if(typeof(activePackage) !== 'undefined' && activePackage !== null) {
            this.setState({ activePackage, selectedHostingPlan: value });
        }
    }

    storageWarning() {
        let currentStorage = parseInt(this.props.activeHostingPlan?.storage.replace('GB', '').trim());
        let newStorage = parseInt(this.state.activePackage?.storage.replace('GB', '').trim());

        if(currentStorage > newStorage) {
            return <Alert style={{marginBottom: '10px'}} type='warning' showIcon message={<span><b>Attention!</b> Your current plan includes an additional {currentStorage-newStorage} GB of storage! Downgrading to a smaller plan may result in reduced storage capacity, which could impact your websites or data. Please ensure your current usage fits the limits of your new plan before upgrading.</span>} />;
        }
    }

    memoryWarning() {
        let currentMemory = this.props.activeHostingPlan?.memory.replace('GB', '').trim();

        if(currentMemory && currentMemory.indexOf('MB') > -1) {
            currentMemory = parseInt(currentMemory.replace('MB', '').trim()) / 1024;
        }

        currentMemory = parseInt(currentMemory);

        let newMemory = this.state.activePackage?.memory.replace('GB', '').trim();

        if(newMemory && newMemory.indexOf('MB') > -1) {
            newMemory = parseInt(newMemory.replace('MB', '').trim()) / 1024;
        }

        newMemory = parseInt(newMemory);

        if(currentMemory > newMemory) {
            return <Alert style={{marginBottom: '10px'}} type='warning' showIcon message={<span><b>Attention!</b> Your current plan includes an additional {currentMemory-newMemory} GB of memory! Downgrading to a smaller plan may result in reduced memory capacity, which could impact your websites or data. Please ensure your current usage fits the limits of your new plan before upgrading.</span>} />;
        }
    }

    cpuWarning() {
        let currentCPU = parseInt(this.props.activeHostingPlan?.cpu.replace('CPU', '').trim());
        let newCPU = parseInt(this.state.activePackage?.cpu.replace('CPU', '').trim());

        if(currentCPU > newCPU) {
            return <Alert style={{marginBottom: '10px'}} type='warning' showIcon message={<span><b>Attention!</b> Your current plan includes an additional {currentCPU-newCPU} cores of CPU! Downgrading to a smaller plan may result in reduced slower request processing speed, which could impact your websites or data. Please ensure your current usage fits the limits of your new plan before upgrading.</span>} />;
        }
    }

    previousStep() {
        this.setState({ step: this.state.step - 1 });
    }

    nextStep() {
        this.setState({ step: this.state.step + 1 });
    }

    dueToday() {
        const start = moment();
        const end = moment(this.props.orderData.order_renewal, "YYYY-MM-DD");
        const currency = this.props.orderData.cost.currency;

        let value;

        if(currency === 'EUR') {
            value = parseFloat(this.state.activePackage.price_eur);
        } else {
            value = parseFloat(this.state.activePackage.price_usd);
        }

        let existingValue = parseFloat(this.props.orderData.cost.amount);

        const costPerDay = value / 30;

        let years = end.diff(start, 'years');
        start.add(years, 'years')
        let months = end.diff(start, 'months');
        start.add(months, 'months')
        let days = end.diff(start, 'days');
        start.add(days, 'days')

        months = months + (years * 12);
        days = days + 1;

        const availableBalance = (months * existingValue) + (days * (existingValue / 30));
        const requiredBalance = (months * value) + (days * costPerDay);
        const dueToday = requiredBalance - availableBalance;

        if(currency === 'EUR') {
            return <Tooltip title={<span>The base rate is in USD. Currency conversion rates are updated daily. Displayed prices are approximate. The final and binding amount is shown on the invoice.</span>}>≈ <MoneyField amount={dueToday} currency={currency} /></Tooltip>
        }

        return <MoneyField amount={dueToday} currency={currency} />
    }

    packageMissingWarning() {
        if(this.state.sharedHostingPlans.filter(plan => plan.guid === this.props.activeHostingPlan.guid).length === 0 && this.state.selectedHostingPlan === null) {
            return <Alert style={{marginBottom: '10px'}} type='warning' showIcon message='Your current plan does not exist anymore, please select a new package below.'/>;
        }
    }

    submitRequest() {
        let data = {
            hosting_account: this.props.hostingAccount,
            new_hosting_plan: this.state.selectedHostingPlan,
        };

        this.setState({ loading: true });

        this.props.modifyHostingAccount(data, (res) => {
            if(typeof(res.data.invoice_id) !== 'undefined') {
                message.success('Success! Redirecting to invoice...');
                this.closeModal(true);

                this.props.history.push('/billing/invoices/' + res.data.invoice_id);
            } else {
                message.success('Hosting account\'s downgrade successfully scheduled!');
                this.closeModal(true)
            }
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                this.setState({ loading: false });
                displayErrors(err.response.data);
            }
        });
    }

    render() {
        const { Option } = Select;

        let content;

        if(this.state.step === 1) {
            content = <>
                {this.packageMissingWarning()}
                {this.storageWarning()}
                {this.memoryWarning()}
                {this.cpuWarning()}

                <Form layout='vertical'>
                    <Form.Item label='New shared hosting plan'>
                        <Select value={this.state.selectedHostingPlan} loading={this.state.loadingSharedHostingPlans}
                                onChange={(value) => this.onPlanChange(value)}>
                            {this.state.sharedHostingPlans.map(plan => (
                                <Option key={plan.guid} value={plan.guid}>{plan.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item hidden={this.state.selectedHostingPlan === null}>
                        {this.state.activePackage !== null ? <Descriptions column={1} bordered size='small'>
                            <Descriptions.Item label={<span>Package</span>}>{this.state.activePackage.name}</Descriptions.Item>
                            <Descriptions.Item label={<span>Monthly price</span>}>
                                {this.props.company.currency === 'EUR' ? <Tooltip
                                        title={<span>The base rate is in USD. Currency conversion rates are updated daily. Displayed prices are approximate. The final and binding amount is shown on the invoice.</span>}>≈ <MoneyField
                                        amount={this.state.activePackage.price_eur} currency='EUR'/></Tooltip> :
                                    <MoneyField amount={this.state.activePackage.price_usd} currency='USD'/>}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={<span>Storage</span>}>{this.state.activePackage.storage}</Descriptions.Item>
                            <Descriptions.Item
                                label={<span>Bandwidth</span>}>{this.state.activePackage.bandwidth}</Descriptions.Item>
                            <Descriptions.Item label={<span>Inodes</span>}>{this.state.activePackage.inodes}</Descriptions.Item>
                            <Descriptions.Item label={<span>RAM</span>}>{this.state.activePackage.memory}</Descriptions.Item>
                            <Descriptions.Item label={<span>CPU</span>}>{this.state.activePackage.cpu}</Descriptions.Item>
                            <Descriptions.Item label={
                                <span>Entry processes</span>}>{this.state.activePackage.entry_processes}</Descriptions.Item>
                            <Descriptions.Item label={
                                <span>Active processes</span>}>{this.state.activePackage.active_processes}</Descriptions.Item>
                            <Descriptions.Item label={
                                <span>Email accounts</span>}>{this.state.activePackage.email_accounts}</Descriptions.Item>
                            <Descriptions.Item
                                label={<span>Databases</span>}>{this.state.activePackage.databases}</Descriptions.Item>
                            <Descriptions.Item
                                label={<span>Subdomains</span>}>{this.state.activePackage.subdomains}</Descriptions.Item>
                            <Descriptions.Item label={
                                <span>Parked domains</span>}>{this.state.activePackage.parked_domains}</Descriptions.Item>
                            <Descriptions.Item
                                label={<span>Addon domains</span>}>{this.state.activePackage.addon_domains}</Descriptions.Item>
                        </Descriptions> : ''}
                    </Form.Item>
                </Form>
            </>
        } else if(this.state.step === 2) {
            let isDowngrade;

            if(this.props.orderData.cost.currency === 'EUR') {
                isDowngrade = this.props.orderData.cost.amount > this.state.activePackage.price_eur;
            } else {
                isDowngrade = this.props.orderData.cost.amount > this.state.activePackage.price_usd;
            }

            if(isDowngrade) {
                content = <Alert type='info' showIcon message={<span>Your hosting account will be scheduled to be updated on <b>{moment(this.props.orderData.order_renewal).format("MMMM D, YYYY")}</b>, at the end of your current billing cycle.</span>}/>;
            } else {
                content = <Descriptions column={1} bordered size='small'>
                    <Descriptions.Item style={{width: '50%'}} label='Current monthly cost'>
                        {this.props.orderData.cost.currency === 'EUR' ? <Tooltip
                            title={<span>The base rate is in USD. Currency conversion rates are updated daily. Displayed prices are approximate. The final and binding amount is shown on the invoice.</span>}>≈ <MoneyField
                            amount={this.props.orderData.cost.amount} currency='EUR'/></Tooltip> : <MoneyField amount={this.props.orderData.cost.amount} currency='USD'/>}
                    </Descriptions.Item>
                    <Descriptions.Item label='New monthly cost'>
                        {this.props.orderData.cost.currency === 'EUR' ? <Tooltip
                            title={<span>The base rate is in USD. Currency conversion rates are updated daily. Displayed prices are approximate. The final and binding amount is shown on the invoice.</span>}>≈ <MoneyField
                            amount={this.state.activePackage.price_eur} currency='EUR'/></Tooltip> : <MoneyField amount={this.state.activePackage.price_usd} currency='USD'/>}
                    </Descriptions.Item>
                    <Descriptions.Item label='Due today'>{this.dueToday()}</Descriptions.Item>
                </Descriptions>
            }
        }

        return <Modal
            open={this.props.showModal}
            title='Modify Hosting Plan'
            footer={<Space>
                <Button disabled={this.state.loading} onChange={() => this.closeModal()}>Close</Button>
                {this.state.step > 1 ? <Button disabled={this.state.loading} onClick={() => this.previousStep()}>Back</Button> : ''}
                {this.state.step === 1 ? <Button disabled={this.state.selectedHostingPlan === null || (this.state.selectedHostingPlan === this.props.orderData.guid)} type='primary' onClick={() => this.nextStep()}>Next</Button> : ''}
                {this.state.step === 2 ? <Button disabled={this.state.loading} type='primary' onClick={() => this.submitRequest()}>Proceed</Button> : ''}
            </Space>}
            onCancel={() => this.closeModal()}>
            {content}
        </Modal>;
    }
}


const mapStateToProps = state => ({
    user: state.auth.user,
    company: state.auth.user.user.company,
});

export default connect(mapStateToProps, { loadSharedHostingPlans, modifyHostingAccount })(ModifyHostingPlanModal);